import { forwardRef } from "react";
import { fmt, MACHINES, DELIVERY } from "@/lib/pricing";
import { ShieldCheck, Wrench, Gem, Sparkles, CreditCard, Truck } from "lucide-react";

const BENEFITS = [
  { icon: ShieldCheck, t: "3 év garancia", d: "Teljes körű garancia minden gépre, a keretszerkezettől a rugókig." },
  { icon: Wrench, t: "Folyamatos alkatrész-utánpótlás", d: "Rugók, kötelek, huzatok és kopó alkatrészek hosszú távon, raktárról." },
  { icon: Gem, t: "Stúdió minőség", d: "Professzionális, napi többórás stúdióhasználatra tervezett kivitel." },
  { icon: Sparkles, t: "Prémium felszereltség", d: "Önfeltekerő kötélrendszer, neoprén rugóhuzatok, komfort lábtartó, csúszásgátló talpak." },
  { icon: CreditCard, t: "Rugalmas fizetés", d: "Egyösszegű vétel vagy 12 / 24 / 36 hónapos részletfizetés – limitált keretből." },
  { icon: Truck, t: "Kiszámítható szállítás", d: "Standard szállítás 96 nap, expressz 48 nap alatt – az ajánlatban rögzítve." },
];

const date = (iso) => new Date(iso).toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric" });

export const QuoteDocument = forwardRef(function QuoteDocument({ quote }, ref) {
  const { customer, items, totals, company, notes } = quote;
  const leaseMonths = [...new Set(items.filter((i) => i.calculation.lease_months).map((i) => i.calculation.lease_months))];
  const companyLine = [company.address, company.tax_id && `Adószám: ${company.tax_id}`, company.email, company.phone, company.website].filter(Boolean);

  return (
    <div ref={ref} data-testid="quote-document" className="quote-doc bg-[#faf8f5] text-[#16181b] relative flex flex-col"
      style={{ width: 794, minHeight: 1123, fontFamily: "Archivo, sans-serif" }}>
      {/* Header band */}
      <div className="bg-[#16181b] text-[#faf8f5] px-14 pt-12 pb-10 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full" style={{ background: "rgba(184,91,53,0.18)" }} />
        <div className="flex items-start justify-between relative">
          <div className="flex items-center gap-3">
            <img src="/logo_mark_white.png" alt="" className="h-10 w-auto object-contain" />
            <div>
              <p className="font-serif-d font-semibold text-lg leading-none">PILATES FACTORY<span className="text-[#b85b35]">.</span></p>
              <p className="eyebrow text-[#8c8882] mt-1.5">High-end reformerek</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-serif-d font-medium text-5xl leading-none tracking-tight">Árajánlat</p>
            <p className="font-mono-d text-sm text-[#b85b35] mt-3">{quote.number}</p>
          </div>
        </div>
        <div className="flex gap-12 mt-9 relative">
          <Meta k="Kiállítva" v={date(quote.created_at)} />
          <Meta k="Érvényes" v={date(quote.valid_until)} />
          <Meta k="Gépek száma" v={`${totals.machines} db`} />
        </div>
      </div>

      <div className="px-14 py-10 flex-1 flex flex-col">
        {/* Parties */}
        <div className="grid grid-cols-2 gap-10">
          <div>
            <p className="eyebrow text-[#8c8882]">Ajánlat címzettje</p>
            <p className="font-serif-d font-semibold text-xl mt-2 leading-tight">{customer.name}</p>
            {customer.company && <p className="text-sm text-[#5c5a57] mt-0.5">{customer.company}</p>}
            <div className="text-sm text-[#5c5a57] mt-2 space-y-0.5">
              {customer.email && <p>{customer.email}</p>}
              {customer.phone && <p>{customer.phone}</p>}
            </div>
          </div>
          <div>
            <p className="eyebrow text-[#8c8882]">Ajánlatot adja</p>
            <p className="font-serif-d font-semibold text-xl mt-2 leading-tight">{company.name}</p>
            <div className="text-sm text-[#5c5a57] mt-2 space-y-0.5">
              {company.address && <p>{company.address}</p>}
              {company.tax_id && <p>Adószám: {company.tax_id}</p>}
              {(company.email || company.phone) && <p>{[company.email, company.phone].filter(Boolean).join(" · ")}</p>}
              {company.website && <p>{company.website}</p>}
            </div>
          </div>
        </div>

        {notes && (
          <p className="font-serif-d italic text-[15px] leading-relaxed text-[#5c5a57] mt-8 border-l-2 border-[#b85b35] pl-4" data-testid="quote-doc-notes">
            {notes}
          </p>
        )}

        {/* Items */}
        <table className="w-full mt-9 text-sm" style={{ borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "38%" }} /><col style={{ width: "9%" }} /><col style={{ width: "17%" }} /><col style={{ width: "21%" }} /><col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr className="text-[#8c8882] text-left font-mono-d text-[10px] uppercase" style={{ letterSpacing: "0.14em" }}>
              <th className="pb-2.5 font-normal border-b border-[#16181b]">Tétel</th>
              <th className="pb-2.5 font-normal border-b border-[#16181b] text-center">Menny.</th>
              <th className="pb-2.5 font-normal border-b border-[#16181b] text-right whitespace-nowrap">Kezdő befiz.</th>
              <th className="pb-2.5 font-normal border-b border-[#16181b] text-right whitespace-nowrap">Havi részlet</th>
              <th className="pb-2.5 font-normal border-b border-[#16181b] text-right">Összesen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const c = it.calculation;
              return (
                <tr key={i} className="border-b border-[#e6e1da] align-top" data-testid={`quote-doc-item-${i}`}>
                  <td className="py-3.5 pr-4">
                    <p className="font-semibold">{MACHINES[it.machine_type].label}</p>
                    <p className="text-xs text-[#8c8882] mt-0.5">
                      {c.payment_label} · {DELIVERY[it.delivery].label} {DELIVERY[it.delivery].sub.toLowerCase()}
                    </p>
                    {c.delivery_fee > 0 && (
                      <p className="text-xs text-[#8c8882]">+ expressz szállítási díj {fmt(c.delivery_fee / it.quantity)} / db</p>
                    )}
                  </td>
                  <td className="py-3.5 text-center font-mono-d">{it.quantity}</td>
                  <td className="py-3.5 pl-3 text-right whitespace-nowrap">{c.lease_months ? fmt(c.down_payment) : "—"}</td>
                  <td className="py-3.5 pl-3 text-right whitespace-nowrap">
                    {c.lease_months ? <>{fmt(c.monthly)}<span className="text-xs text-[#8c8882]"> × {c.lease_months} hó</span></> : "—"}
                  </td>
                  <td className="py-3.5 pl-3 text-right font-semibold whitespace-nowrap">{fmt(c.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Summary */}
        <div className="grid grid-cols-12 gap-8 mt-8">
          <div className="col-span-7 text-xs text-[#5c5a57] leading-relaxed self-end">
            <p>Az árak nettó árak, az ÁFA-t nem tartalmazzák. A részletfizetéses konstrukció esetén a kezdő befizetés
              a megrendeléskor, a havi részletek a futamidő alatt havonta esedékesek. Az expressz szállítási díj a kezdő
              befizetés részét képezi.</p>
            <p className="mt-2">Az ajánlat a feltüntetett érvényességi időn belül, a részletfizetési keret és a készlet erejéig érvényes.</p>
          </div>
          <div className="col-span-5 bg-[#16181b] text-[#faf8f5] rounded-2xl p-6" data-testid="quote-doc-summary">
            <div className="space-y-2 text-sm">
              {totals.down_payment > 0 && <SumRow k="Kezdő befizetés" v={fmt(totals.down_payment)} />}
              {totals.monthly > 0 && (
                <SumRow k={`Havi részlet${leaseMonths.length === 1 ? ` · ${leaseMonths[0]} hó` : ""}`} v={`${fmt(totals.monthly)} / hó`} accent />
              )}
              {totals.one_time > 0 && <SumRow k="Egyösszegű tételek" v={fmt(totals.one_time)} />}
            </div>
            <div className="border-t border-[#2a2d32] mt-4 pt-4">
              <p className="eyebrow text-[#8c8882]">Konstrukció teljes értéke</p>
              <p className="font-serif-d font-semibold text-[28px] leading-tight mt-1">{fmt(totals.grand_total)}</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-10" style={{ pageBreakInside: "avoid" }}>
          <p className="eyebrow text-[#b85b35]">Amit minden Pilates Factory gép mellé adunk</p>
          <div className="grid grid-cols-3 gap-x-6 gap-y-5 mt-4">
            {BENEFITS.map(({ icon: Icon, t, d }) => (
              <div key={t} className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full border border-[#b85b35] flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-[#b85b35]" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold leading-tight">{t}</p>
                  <p className="text-[11px] text-[#5c5a57] leading-snug mt-1">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-8">
          <div className="border-t border-[#e6e1da] pt-4 flex items-center justify-between text-[10px] text-[#8c8882]">
            <p className="max-w-[520px] leading-relaxed">
              <span className="font-semibold text-[#16181b]">{company.name}</span>
              {companyLine.length > 0 && ` · ${companyLine.join(" · ")}`}
            </p>
            <p className="font-mono-d whitespace-nowrap">{quote.number}</p>
          </div>
        </div>
      </div>
    </div>
  );
});

const Meta = ({ k, v }) => (
  <div>
    <p className="eyebrow text-[#8c8882]">{k}</p>
    <p className="text-sm font-medium mt-1">{v}</p>
  </div>
);

const SumRow = ({ k, v, accent }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#8c8882] whitespace-nowrap">{k}</span>
    <span className={`whitespace-nowrap ${accent ? "text-[#b85b35] font-semibold" : "text-[#d8d4ce]"}`}>{v}</span>
  </div>
);
